import React from 'react';
import { useCurrentUser } from '@economic/react-app';

import { setToastPosition, resetToastPosition } from '../util/utilities/toastPosition';

declare global {
    interface Window {
        LiveChatWidget: any;
        __lc: any;
    }
}

type LiveChatProps = {
    children: (props: { toggle: () => void; show: () => void; hide: () => void }) => JSX.Element;
};

export default function LiveChat({ children }: LiveChatProps) {
    const user = useCurrentUser();

    React.useEffect(() => {
        if (!window.LiveChatWidget) {
            const { license, name, email, ...profile } = user.extra.liveChatProfile;

            const licenseNr = Number(license);

            window.__lc = window.__lc || {};
            window.__lc.license = licenseNr;

            function handle(args) {
                return window.LiveChatWidget._h !== null
                    ? window.LiveChatWidget._h.apply(null, args)
                    : window.LiveChatWidget._q.push(args);
            }

            window.LiveChatWidget = {
                _q: [],
                _h: null,
                _v: '2.0',

                on: function() {
                    handle(['on', [].slice.call(arguments)]);
                },
                off: function() {
                    handle(['off', [].slice.call(arguments)]);
                },
                get: function() {
                    if (!window.LiveChatWidget._h) {
                        throw new Error("[LiveChatWidget] You can't use getters before load.");
                    }
                    return handle(['get', [].slice.call(arguments)]);
                },
                call: function() {
                    handle(['call', [].slice.call(arguments)]);
                },
            };

            // when chat widget is minimized, hide the widget entirely if customer didn't start a chat
            window.LiveChatWidget.on('visibility_changed', function({ visibility }) {
                try {
                    const { status } = window.LiveChatWidget.get('customer_data');
                    if (visibility === 'minimized' && status !== 'chatting' && status !== 'queued') {
                        window.LiveChatWidget.call('hide');
                    }

                    // Set visibility after half a second so that the animation transition of live chat popup is completed
                    setTimeout(() => {
                        if (visibility === 'hidden') {
                            resetToastPosition();
                        } else {
                            setToastPosition(document.querySelector<HTMLElement>('#chat-widget-container'));
                        }
                    }, 500);
                } catch {}
            });

            window.LiveChatWidget.on('new_event', function(event) {
                // Display minimized widget icon when new incoming message and livechat wdiget is hidden
                try {
                    const { visibility } = window.LiveChatWidget.get('state');
                    if (visibility === 'hidden' && event.author.type === 'agent' && !event.greeting) {
                        window.LiveChatWidget.call('minimize');
                    }
                } catch {}
            });

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://cdn.livechatinc.com/tracking.js`;
            script.onload = () => {
                window.LiveChatWidget.call('hide');
                window.LiveChatWidget.call('set_customer_name', name);
                window.LiveChatWidget.call('set_customer_email', email);
                window.LiveChatWidget.call('set_session_variables', profile);
            };
            document.head.appendChild(script);
        }

        return () => {
            if (window.LiveChatWidget) {
                window.LiveChatWidget.call('destroy');
            }
        };
    }, []);

    const toggle = React.useCallback(() => {
        try {
            const { visibility } = window.LiveChatWidget.get('state');
            window.LiveChatWidget.call(visibility === 'hidden' ? 'maximize' : 'hide');
        } catch {}
    }, []);

    return children({
        toggle,
        show: () => window.LiveChatWidget.call('maximize'),
        hide: () => window.LiveChatWidget.call('hide'),
    });
}

export function showChat() {
    try {
        window.LiveChatWidget.call('maximize');
    } catch {}
}
